// Turning a spreadsheet's own formatting into markup a tiptap editor will keep. Kept apart
// from the extensions so it can be tested without pulling an editor in behind it.

// A node keeps only the attributes its schema declares - for a cell that is colspan,
// rowspan and colwidth - so a pasted table arrives with its structure and none of its
// formatting. Carry a whitelist of visual properties through instead. A whitelist rather
// than the whole style attribute, so nothing arbitrary rides into a customer-facing quote.
const KEPT_CELL_STYLES = [
    'background-color', 'background', 'color', 'text-align', 'vertical-align',
    'font-weight', 'font-style', 'text-decoration', 'font-size', 'border', 'border-top',
    'border-right', 'border-bottom', 'border-left', 'width', 'height',
];

function keptStyleOf(element) {
    const kept = [];
    for (const prop of KEPT_CELL_STYLES) {
        const value = element.style?.getPropertyValue(prop);
        if (value) kept.push(`${prop}:${value}`);
    }
    // Excel still emits the old bgcolor attribute alongside (or instead of) a style.
    const bgcolor = element.getAttribute && element.getAttribute('bgcolor');
    if (bgcolor && !element.style?.getPropertyValue('background-color')) kept.push(`background-color:${bgcolor}`);
    // align/valign are the same story: presentational attributes older than CSS that
    // spreadsheets still emit.
    const align = element.getAttribute && element.getAttribute('align');
    if (align && !element.style?.getPropertyValue('text-align')) kept.push(`text-align:${align}`);
    const valign = element.getAttribute && element.getAttribute('valign');
    if (valign && !element.style?.getPropertyValue('vertical-align')) kept.push(`vertical-align:${valign}`);
    return kept.length ? kept.join(';') + ';' : null;
}

// A document made by DOMParser has no browsing context, so its <style> elements are never
// parsed and carry no sheet. Read the CSS with the engine's own parser instead of by hand,
// while keeping it away from this page's styling.
function parseCssRules(cssText) {
    // A constructable sheet is parsed in isolation and can never apply to anything.
    try {
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(cssText);
        return [...sheet.cssRules];
    } catch (e) { /* engine without constructable stylesheets - fall through */ }

    // Otherwise the only parser to hand is the live document's. media="not all" means the
    // rules are parsed and matched against nothing, so a spreadsheet's own `table { }` rule
    // cannot reach the app in the moment the element is attached.
    let host = null;
    try {
        host = document.createElement('style');
        host.setAttribute('media', 'not all');
        host.textContent = cssText;
        document.head.appendChild(host);
        return host.sheet ? [...host.sheet.cssRules] : [];
    } catch (e) {
        return [];
    } finally {
        if (host) host.remove();
    }
}

/**
 * Excel, Excel for the web and WPS Spreadsheets write a cell's colours, borders and weight
 * into a <style> block as classes and leave the cell itself carrying nothing but
 * `class=xl65`. Only the inline style attribute is read above, and a class means nothing to
 * it, so a table copied from any of them arrived with its structure and none of its
 * formatting. Fold each rule onto the elements it matches before the editor parses the
 * markup, which is the last moment the stylesheet still exists.
 */
function resolveStyleBlocks(html) {
    if (!html || typeof DOMParser === 'undefined' || !/<style/i.test(html)) return html;

    let doc;
    try {
        doc = new DOMParser().parseFromString(html, 'text/html');
    } catch (e) {
        return html;
    }

    const styleElements = [...doc.querySelectorAll('style')];
    if (styleElements.length === 0) return html;

    for (const styleElement of styleElements) {
        const rules = parseCssRules(styleElement.textContent || '');
        for (const rule of rules) {
            if (!rule.selectorText || !rule.style) continue;
            let matched;
            try {
                matched = doc.querySelectorAll(rule.selectorText);
            } catch (e) {
                // A selector the spreadsheet emitted that this browser will not parse.
                continue;
            }
            for (const element of matched) {
                for (let i = 0; i < rule.style.length; i++) {
                    const prop = rule.style[i];
                    // An inline declaration is the more specific one and wins, as it would
                    // have in the source document.
                    if (element.style.getPropertyValue(prop)) continue;
                    element.style.setProperty(prop, rule.style.getPropertyValue(prop));
                }
            }
        }
        styleElement.remove();
    }

    return doc.body.innerHTML;
}

export { KEPT_CELL_STYLES, keptStyleOf, resolveStyleBlocks };
